import cloneDeep from "lodash/cloneDeep";
import moment from "moment";

import { EncounterCategory } from "models/encounterServiceEntity";

import { DomainType } from "../../../models/domain";
import { gridsHaveIncorrectSpecificFields } from "../../../reducers/Helpers/encounterCodeGrid";
import {
  MAX_FINANCIAL_CLASS_LENGTH,
  MAX_PROVIDER_LENGTH,
  checkDDLFieldLength,
} from "../../../services/encounter/encounterMapping";
import { toFormattedDate } from "../../../utils/date";
import {
  isDateValidator,
  isRequiredFieldValidator,
  longTextValidator,
  patternValidator,
} from "../../../validators/simpleValidators";
import { correctAgeInYearsField, validateAgeInYearsField } from "../utils/calculateAge";
import calculateLOS from "../utils/calculateLOS";
import { isIncorrectWeight, parseWeight } from "../utils/calculateWeight";

import type { EncounterEntity } from "../../../models/encounterEntity";
import type { EncounterValidation } from "../../../models/encounterErrors";
import type { FacilitySettingsState } from "../../../models/facilitySettings";
import type { ChoiceListsState, IdDescriptionBase } from "../../../models/patientEncounter";
import type { ValuesRow } from "../../../models/valuesGrid";

// client side validations without required checking
export function validateEncounter(
  encounter: EncounterEntity,
  facilitySettings: FacilitySettingsState,
  choiceLists: ChoiceListsState,
  allowFutureDate: boolean,
): EncounterValidation {
  // check coderows for critical errors
  const gridsHaveDateErrors = gridsHaveIncorrectSpecificFields(
    encounter,
    choiceLists.providers,
    "date",
    allowFutureDate,
  );
  const gridsHaveProviderErrors = gridsHaveIncorrectSpecificFields(
    encounter,
    choiceLists.providers,
    "provider",
  );
  let gridsError = gridsHaveDateErrors ? "Codes have incorrect dates" : "";
  if (gridsHaveProviderErrors && !gridsHaveDateErrors) {
    gridsError = "The value for Providers is too long for some code rows";
  }

  const criticalError =
    checkCriticalError(encounter, choiceLists, allowFutureDate) ||
    gridsHaveDateErrors ||
    gridsHaveProviderErrors;
  return {
    // check length <= 24
    accountNumber: validateAccountNumber(encounter, false),
    admitDate: validateAdmitDate(encounter, false, "", { allowFutureDate, checkRanges: true }),
    // check for pattern and special validations
    ageInYears: validateAgeInYears(encounter, false, false, ""),
    dateOfBirth: validateDateOfBirth(encounter, false, "", true),
    dischargeDate: validateDischargeDate(encounter, false, "", {
      allowFutureDate,
      checkRanges: true,
    }),
    // check length <= 29
    firstName: validateNameField(encounter.firstName, 29, false, ""),
    // check length <= 50
    lastName: validateNameField(encounter.lastName, 50, false, ""),
    // check length <= 24
    medicalRecordNumber: validateMedicalRecordNumber(encounter, false, ""),
    // check length <= 1
    middleInitial: validateNameField(encounter.middleInitial, 1, false, ""),
    diagnoses: "",
    // check for pattern
    birthWeight: validateBirthWeight(encounter, false, ""),
    // only numbers
    overrideLOS: validateOverrideLOS(encounter, false, ""),
    patientStatus: "",
    reviewDate: validateReviewDate(encounter, false),
    sex: "",
    // only numbers
    totalCodingTime: validateTotalCodingTime(encounter),
    // always required
    type: isRequiredFieldValidator(encounter.type, "Encounter type cannot be empty"),

    service: "",
    serviceLocation:
      encounter.domain === DomainType.Practice ?
        isRequiredFieldValidator(encounter.serviceLocation, "Service Location cannot be empty")
      : "",
    ofa: "",
    // check for the presence in the list or <= MAX_FINANCIAL_CLASS_LENGTH
    financialClass: validateFinancialClass(choiceLists.financialClasses, encounter.financialClass),
    billType: "",
    // check for the presence in the list or <= MAX_PROVIDER_LENGTH
    attendingMd: validateAttendingMd(choiceLists.providers, encounter.attendingMd, encounter),
    managedCareFlag: "",
    placeOfService: "",
    // nothing
    totalCharges: validateTotalCharges(encounter, false, ""),
    thirdPartyLiability: "",
    recordStatus: "",
    // always required
    facility: isRequiredFieldValidator(encounter.facility, "Facility cannot be empty"),
    conditionCodes: "",
    valueCodes: "",
    visitReasons: "",
    admitDX: "",
    admitDXMissing: "",
    clientPreventsSave: criticalError || checkPreventSave(encounter),
    clientCriticalError: criticalError,
    gridsError,
  };
}

// fields with critical errors
export const SUPPLEMENTARY_CRITICAL_ERROR_FIELDS: string[] = [];

interface ValidationConfig {
  allowFutureDate?: boolean;
  checkRanges?: boolean;
}

// client side validations to find all client side critical errors
export function validateEncounterForCriticalErrors(
  encounter: EncounterEntity,
  facilitySettings: FacilitySettingsState,
  choiceLists: ChoiceListsState,
  config: ValidationConfig = {
    checkRanges: false,
    allowFutureDate: false,
  },
): EncounterValidation {
  return {
    accountNumber: "",
    admitDate: validateAdmitDate(encounter, false, "", config),
    ageInYears: validateAgeInYears(encounter, false, false, ""),
    dateOfBirth: validateDateOfBirth(encounter, false, "", config.checkRanges),
    dischargeDate: validateDischargeDate(encounter, false, "", config),
    firstName: "",
    lastName: "",
    medicalRecordNumber: "",
    middleInitial: "",
    diagnoses: "",
    birthWeight: validateBirthWeight(encounter, false, ""),
    overrideLOS: "",
    patientStatus: "",
    reviewDate: validateReviewDate(encounter, config.checkRanges),
    sex: "",
    totalCodingTime: "",
    type: "",
    service: "",
    serviceLocation: "",
    ofa: "",
    financialClass: validateFinancialClass(choiceLists.financialClasses, encounter.financialClass),
    billType: "",
    attendingMd: validateAttendingMd(choiceLists.providers, encounter.attendingMd, encounter),
    managedCareFlag: "",
    placeOfService: "",
    totalCharges: "",
    thirdPartyLiability: "",
    recordStatus: "",
    facility: "",
    conditionCodes: "",
    valueCodes: "",
    visitReasons: "",
    admitDX: "",
    admitDXMissing: "",
  };
}

function validateAccountNumber(encounter: EncounterEntity, checkRequired: boolean): string {
  const value = encounter.accountNumber;

  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(value, "Missing Account Number");
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  const longTextResult = longTextValidator(value, 24);
  if (longTextResult) {
    return longTextResult;
  }

  return "";
}

function validateNameField(
  value: string | undefined,
  maxLength: number,
  checkRequired: boolean,
  requiredString: string,
): string {
  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(value, requiredString);
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  const longTextResult = longTextValidator(value, maxLength);
  if (longTextResult) {
    return longTextResult;
  }

  return "";
}

function validateAgeInYears(
  encounter: EncounterEntity,
  checkRequired: boolean,
  isDOBRequired: boolean,
  requiredStringEnd: string,
): string {
  const { ageInYears } = encounter;
  const { dateOfBirth } = encounter;

  // if DOB is required - validation is shown for the DOB
  if (checkRequired && !isDOBRequired) {
    const succeeded = !!(ageInYears || dateOfBirth);
    if (!succeeded) {
      return `Field "Patient Age" is required${requiredStringEnd}`;
    }
  }

  return validateAgeInYearsField(ageInYears);
}

function validateDateOfBirth(
  encounter: EncounterEntity,
  checkRequired: boolean,
  requiredStringEnd: string,
  checkRanges = false,
): string {
  const value = encounter.dateOfBirth;

  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(
      value,
      `Field "Date of Birth" is required${requiredStringEnd}`,
    );
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  const isDateValidatorResult = isDateValidator(value, "Invalid date of birth", true);
  if (isDateValidatorResult) {
    return isDateValidatorResult;
  }

  const date = toFormattedDate(value);
  if (!date) {
    return "";
  }

  // check DOB <= current and DOB <= AD and DOB <= DD
  if (!checkRanges) {
    return "";
  }

  if (moment().isBefore(date)) {
    return "Invalid date of birth";
  }

  const admitDate = toFormattedDate(encounter.admitDate);
  if (admitDate) {
    if (date > admitDate) {
      return "Admit and/or discharge date precedes date of birth.";
    }
  }

  const dischargeDate = toFormattedDate(encounter.dischargeDate);
  if (dischargeDate) {
    if (date > dischargeDate) {
      return "Admit and/or discharge date precedes date of birth.";
    }
  }

  return "";
}

function validateAdmitDate(
  encounter: EncounterEntity,
  checkRequired: boolean,
  requiredStringEnd: string,
  config: ValidationConfig,
): string {
  const value = encounter.admitDate;

  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(
      value,
      `Field "Admit Date" is required${requiredStringEnd}`,
    );
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  const isDateValidatorResult = isDateValidator(
    value,
    !config.allowFutureDate ? "Incorrect Admission Date" : undefined,
  );
  if (isDateValidatorResult) {
    return isDateValidatorResult;
  }

  // check AD <= current and AD >= DOB
  if (!config.checkRanges) {
    return "";
  }

  const date = toFormattedDate(value);
  if (!date) {
    return "";
  }

  if (!config.allowFutureDate && moment().isBefore(date)) {
    return "Incorrect Admission Date";
  }

  const dateOfBirth = toFormattedDate(encounter.dateOfBirth);
  if (dateOfBirth) {
    if (date < dateOfBirth) {
      return "Admit and/or discharge date precedes date of birth.";
    }
  }

  return "";
}

function validateDischargeDate(
  encounter: EncounterEntity,
  checkRequired: boolean,
  requiredStringEnd: string,
  config: ValidationConfig,
): string {
  const value = encounter.dischargeDate;

  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(
      value,
      `Field "Discharge Date" is required${requiredStringEnd}`,
    );
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  const isDateValidatorResult = isDateValidator(
    value,
    !config.allowFutureDate ? "Incorrect Discharge Date" : undefined,
  );
  if (isDateValidatorResult) {
    return isDateValidatorResult;
  }

  const date = toFormattedDate(value);
  if (!date) {
    return "";
  }

  // check DD <= current and AD <= DD and DOB <= DD
  if (!config.checkRanges) {
    return "";
  }

  if (!config.allowFutureDate && moment().isBefore(date)) {
    return "Incorrect Discharge Date";
  }

  const dateOfBirth = toFormattedDate(encounter.dateOfBirth);
  if (dateOfBirth) {
    if (date < dateOfBirth) {
      return "Admit and/or discharge date precedes date of birth.";
    }
  }

  const admitDate = toFormattedDate(encounter.admitDate);
  if (admitDate) {
    if (date < admitDate) {
      return "Discharge date before admit date";
    }
  }

  return "";
}

function validateReviewDate(encounter: EncounterEntity, checkRanges = false): string {
  const value = encounter.reviewDate;

  // can be future date
  const isDateValidatorResult = isDateValidator(value);
  if (isDateValidatorResult) {
    return isDateValidatorResult;
  }

  // check RD >= AD
  if (!checkRanges) {
    return "";
  }

  const date = toFormattedDate(value);
  if (!date) {
    return "";
  }

  const admitDate = toFormattedDate(encounter.admitDate);
  if (admitDate) {
    if (date < admitDate) {
      return "The review date must be on or after the Admit date";
    }
  } else {
    return "Please specify an admit date prior to specifying a review date";
  }

  return "";
}

function validateMedicalRecordNumber(
  encounter: EncounterEntity,
  checkRequired: boolean,
  requiredStringEnd: string,
): string {
  const value = encounter.medicalRecordNumber;

  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(
      value,
      `Field "Medical Record Number" is required${requiredStringEnd}`,
    );
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  const longTextResult = longTextValidator(value, 24);
  if (longTextResult) {
    return longTextResult;
  }

  return "";
}

function validateBirthWeight(
  encounter: EncounterEntity,
  checkRequired: boolean,
  requiredStringEnd: string,
): string {
  const value = encounter.birthWeight;

  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(
      value,
      `Field "Birth Weight" is required${requiredStringEnd}`,
    );
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  if (!value) {
    return "";
  }

  const checkValue = parseWeight(value);

  if (isIncorrectWeight(checkValue)) {
    return checkValue;
  }

  return "";
}

function validateOverrideLOS(
  encounter: EncounterEntity,
  checkRequired: boolean,
  requiredStringEnd: string,
  checkRanges = false,
): string {
  const value = encounter.overrideLOS;

  if (checkRequired) {
    if (value === undefined || (value as unknown as string) === "") {
      return `Field "Override LOS" is required${requiredStringEnd}`;
    }
  }

  if (!value) {
    return "";
  }

  const pattern = "^[0-9]{0,5}$";
  const patternValidatorResult = patternValidator(`${value}`, pattern);
  if (patternValidatorResult) {
    return patternValidatorResult;
  }

  const los = calculateLOS(encounter);

  if (checkRanges) {
    const succeeded = !!(!value || Number(value) <= los);
    if (!succeeded) {
      return "Override LOS cannot be greater than encounter LOS";
    }
  }

  return "";
}

function validateTotalCharges(
  encounter: EncounterEntity,
  checkRequired: boolean,
  requiredStringEnd: string,
): string {
  const value = encounter.totalCharges;

  if (checkRequired) {
    if (value === undefined || (value as unknown as string) === "") {
      return `Field "Total Charges" is required${requiredStringEnd}`;
    }
  }

  return "";
}

function validateTotalCodingTime(encounter: EncounterEntity): string {
  const value = encounter.totalCodingTime;

  if (!value) {
    return "";
  }

  const pattern = "^[0-9]{0,5}$";
  const patternValidatorResult = patternValidator(value, pattern);
  if (patternValidatorResult) {
    return patternValidatorResult;
  }

  return "";
}

function validateFinancialClass(
  choiceList: IdDescriptionBase[],
  value?: string,
  checkRequired?: boolean,
  requiredStringEnd?: string,
): string {
  if (checkRequired) {
    const isRequiredValidatorResult = isRequiredFieldValidator(
      value,
      `Field "Financial Class" is required${requiredStringEnd}`,
    );
    if (isRequiredValidatorResult) {
      return isRequiredValidatorResult;
    }
  }

  if (value && !checkDDLFieldLength(choiceList, MAX_FINANCIAL_CLASS_LENGTH, value)) {
    return "The Financial Class value exceeds the maximum allowed limit.";
  }

  return "";
}

function validateAttendingMd(
  choiceList: IdDescriptionBase[],
  value?: string,
  encounter: EncounterEntity,
): string {
  const isProfessional = encounter.ValidationResult?.Category === EncounterCategory.Professional;
  const providerLabel = isProfessional ? "Rendering Provider" : "Attending MD";

  if (value && !checkDDLFieldLength(choiceList, MAX_PROVIDER_LENGTH, value)) {
    return `The ${providerLabel} value exceeds the maximum allowed limit.`;
  }

  return "";
}

export function checkDates(
  dateOfBirth?: string,
  admitDate?: string,
  dischargeDate?: string,
  compareADDD = false,
): boolean {
  const ad = toFormattedDate(admitDate);
  const dd = toFormattedDate(dischargeDate);

  if (dateOfBirth) {
    const dob = toFormattedDate(dateOfBirth);

    if (dob && ad) {
      if (dob > ad) {
        return true;
      }
    }

    if (dob && dd) {
      if (dob > dd) {
        return true;
      }
    }
  }

  if (compareADDD && ad && dd && ad > dd) {
    return true;
  }

  return false;
}

function checkCriticalError(
  encounter: EncounterEntity,
  choiceLists: ChoiceListsState,
  allowFutureDate: boolean,
): boolean {
  // not empty and incorrect admitDate
  if (
    encounter.admitDate &&
    isDateValidator(encounter.admitDate, !allowFutureDate ? "Incorrect Admission Date" : undefined)
  ) {
    return true;
  }
  // not empty and incorrect dateOfBirth
  if (
    encounter.dateOfBirth &&
    isDateValidator(encounter.dateOfBirth, "Invalid date of birth", true)
  ) {
    return true;
  }
  // not empty and incorrect dischargeDate
  if (
    encounter.dischargeDate &&
    isDateValidator(
      encounter.dischargeDate,
      !allowFutureDate ? "Incorrect Discharge Date" : undefined,
    )
  ) {
    return true;
  }

  // check here ReviewDate after tab enabling

  // not empty and incorrect birthWeight
  if (encounter.birthWeight) {
    const birthWeightValue = parseWeight(encounter.birthWeight || "");
    if (isIncorrectWeight(birthWeightValue)) {
      return true;
    }
  }

  // not empty and incorrect ageInYears
  if (encounter.ageInYears) {
    const ageInYearsValue = correctAgeInYearsField(encounter.ageInYears);
    if (validateAgeInYearsField(ageInYearsValue)) {
      return true;
    }
  }

  // too long Financial Class
  if (
    encounter.financialClass &&
    !checkDDLFieldLength(
      choiceLists.financialClasses,
      MAX_FINANCIAL_CLASS_LENGTH,
      encounter.financialClass,
    )
  ) {
    return true;
  }

  // too long AttendingMd
  if (
    encounter.attendingMd &&
    !checkDDLFieldLength(choiceLists.providers, MAX_PROVIDER_LENGTH, encounter.attendingMd)
  ) {
    return true;
  }

  return false;
}

function checkPreventSave(encounter: EncounterEntity): boolean {
  // DOB > AD or DOB > DD or AD > DD
  if (checkDates(encounter.dateOfBirth, encounter.admitDate, encounter.dischargeDate, true)) {
    return true;
  }

  return false;
}

export function validateConditionCodesLength(valuesCodeRow: ValuesRow): ValuesRow {
  const rowWithErrors = cloneDeep(valuesCodeRow);

  if (rowWithErrors.Validations?.length) {
    return rowWithErrors;
  }

  rowWithErrors.Validations = [];
  if (valuesCodeRow.ValueCode || valuesCodeRow.Amount) {
    return valuesCodeRow;
  }

  return rowWithErrors;
}
